import "dotenv/config";

import fs from "node:fs";
import {
  createServer as createHttpServer,
  Server as HttpServer,
} from "node:http";
import https, { Server as HttpsServer } from "node:https";
import path from "node:path";

import App from "./app";
import { attendanceReminderService } from "./services/attendance-reminder-service";
import { redisService } from "./services/redis-service";
import { studentAutoCheckoutService } from "./services/student-auto-checkout-service";
import { socketServer } from "./socket/socket-server";
import { logger } from "./utils/logger";
import { loadSSLCertificates } from "./utils/ssl-config";

/**
 * Start the server
 */
async function startServer(): Promise<void> {
  // Get PORT from .env file
  const PORT = Number.parseInt(process.env.PORT ?? "3000", 10);
  const NODE_ENV = process.env.NODE_ENV ?? "development";

  // Initialize Redis service (optional - server will continue without it)
  try {
    await redisService.initialize();
    logger.info("✅ Redis service initialized");
  } catch (error) {
    logger.warn(
      "⚠️ Redis service initialization failed, continuing without Redis:",
      error instanceof Error ? error.message : String(error),
    );
  }

  const app = new App();
  const expressApp = app.getApp();

  // Error handlers
  let server: HttpServer | HttpsServer | undefined;

  const exitHandler = () => {
    if (server) {
      server.close(() => {
        logger.info("Server closed");
        // eslint-disable-next-line unicorn/no-process-exit
        process.exit(1);
      });
    } else {
      // eslint-disable-next-line unicorn/no-process-exit
      process.exit(1);
    }
  };

  const unexpectedErrorHandler = (error: Error) => {
    logger.error("Unexpected error:", error);
    exitHandler();
  };

  process.on("SIGTERM", () => {
    logger.info("SIGTERM received");
    if (server) {
      server.close();
    }
  });

  const rateLimitingStatus =
    process.env.ENABLE_RATE_LIMITING === "true" ? "✅ Enabled" : "❌ Disabled";

  // Determine if we should use HTTPS
  // In production with reverse proxy (cPanel), typically run HTTP and let proxy handle SSL
  // Only use HTTPS if explicitly enabled AND certificates exist
  const sslKeyPath =
    process.env.SSL_KEY_PATH ?? path.join(process.cwd(), "ssl", ".key");
  const sslCertExists = fs.existsSync(sslKeyPath);

  // Use HTTPS only if:
  // 1. Explicitly enabled via USE_HTTPS=true AND certificates exist
  // 2. OR in production with certificates AND no reverse proxy (BEHIND_PROXY not set)
  const useHTTPS =
    (process.env.USE_HTTPS === "true" && sslCertExists) ||
    (NODE_ENV === "production" &&
      sslCertExists &&
      process.env.BEHIND_PROXY !== "true");

  if (useHTTPS) {
    try {
      const sslOptions = loadSSLCertificates();
      const httpsServer = https.createServer(sslOptions, expressApp);

      server = httpsServer.listen(PORT, () => {
        logger.info(`🚀 HTTPS Server is running on port ${PORT}`);
        logger.info(`🔗 Health check: https://localhost:${PORT}/health`);
        logger.info(`📚 API docs: https://localhost:${PORT}/api/v1/docs`);
        logger.info(`🌍 Environment: ${NODE_ENV}`);
        logger.info(
          `📧 Email service: ${process.env.SMTP_HOST ?? "Not configured"}`,
        );
        logger.info(`⚡ Rate limiting: ${rateLimitingStatus}`);
        logger.info("🔒 SSL certificates loaded successfully");

        // Initialize Socket.io server
        socketServer.initialize(httpsServer);
        logger.info("✅ Socket.io server initialized");

        // Start attendance reminder service
        attendanceReminderService.start();
        logger.info("✅ Attendance reminder service started");

        // Start student auto check-out job (auto check-out when class ends)
        studentAutoCheckoutService.start();
        logger.info("✅ Student auto check-out service started");
      });

      server.on("error", (error: Error) => {
        logger.error("HTTPS server error:", error);
        throw error;
      });
    } catch (error: unknown) {
      logger.error("Failed to start HTTPS server:", error);
      if (error instanceof Error) {
        logger.error(error.message);
      }
      throw error;
    }
  } else {
    // Create HTTP server for all other environments (production, staging, etc.)
    const httpServer = createHttpServer(expressApp);

    server = httpServer.listen(PORT, () => {
      logger.info(`🚀 HTTP Server is running on port ${PORT}`);
      logger.info(`🔗 Health check: http://localhost:${PORT}/health`);
      logger.info(`📚 API docs: http://localhost:${PORT}/api/v1/docs`);
      logger.info(`🌍 Environment: ${NODE_ENV}`);
      logger.info(
        `📧 Email service: ${process.env.SMTP_HOST ?? "Not configured"}`,
      );
      logger.info(`⚡ Rate limiting: ${rateLimitingStatus}`);

      // Initialize Socket.io server
      socketServer.initialize(httpServer);
      logger.info("✅ Socket.io server initialized");

      // Start attendance reminder service
      attendanceReminderService.start();
      logger.info("✅ Attendance reminder service started");

      // Start student auto check-out job (auto check-out when class ends)
      studentAutoCheckoutService.start();
      logger.info("✅ Student auto check-out service started");
    });

    server.on("error", (error: Error) => {
      logger.error("HTTP server error:", error);
      throw error;
    });
  }
}

// Handle unhandled promise rejections and uncaught exceptions early
process.on("uncaughtException", (error: Error) => {
  logger.error("Uncaught Exception:", error);
  // eslint-disable-next-line unicorn/no-process-exit
  process.exit(1);
});

process.on("unhandledRejection", (reason: unknown) => {
  logger.error("Unhandled Rejection:", reason);
  // eslint-disable-next-line unicorn/no-process-exit
  process.exit(1);
});

// Start the server
startServer().catch((error: unknown) => {
  logger.error("Failed to start server:", error);
  // eslint-disable-next-line unicorn/no-process-exit
  process.exit(1);
});
